import clsx from 'clsx';
import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { ScrollToTop } from '../../../common/table/helper';
import { HL7FilterAction, HL7FilterDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './HL7FiltersTable.module.scss';
import InfoIcon from '@lmt/unicorn-shared/app/icons/info.svg?react';
import DragAndDropIcon from '@lmt/unicorn-shared/app/icons/drag-and-drop.svg?react';
import i18next from 'i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { HL7MessageTypeNamesDict } from '@lmt/unicorn-shared/data/messageTypeNamesDict';
import { CheckBox } from '../../../common/ui-kit/CheckBox';
import { TableDotMenu } from '../../../common/table/TableDotMenu';

const DragHandle = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div
    {...props}
    ref={ref}
    title={i18next.t('calendar.color_settings.list.drag_and_drop_tooltip')}
  >
    <DragAndDropIcon className={styles.icon} />
  </div>
));

const SortableRowWrapper = (props: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.data.position });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Row
      {...props}
      {...attributes}
      listeners={listeners}
      style={style}
      ref={setNodeRef}
    />
  );
};

const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
  const {
    data,
    editFilter,
    removeFilter,
    onEnabledChanged,
    isDefault,
    style,
    listeners,
    ...rest
  } = props;

  const i18n = useScopedTranslation('hl7.filters.list');
  return (
    <div {...rest} style={style} className={clsx(styles.row, styles.rowData)}>
      <div>{!isDefault && <DragHandle ref={ref} {...listeners} />}</div>
      <div className={styles.cell}>
        {data.messageType === null
          ? i18n.t('fallback_filter')
          : HL7MessageTypeNamesDict[data.messageType]}
      </div>
      <div className={styles.cell}>
        {i18n.t(data.action === HL7FilterAction.Accept ? 'accept' : 'reject')}
      </div>
      <div className={styles.cell}>
        <CheckBox
          checked={data.enabled}
          onChange={(e) => onEnabledChanged(data.id, e.currentTarget.checked)}
        />
      </div>
      <div
        className={styles.optionCell}
        onClick={(event: any) => event.stopPropagation()}
      >
        {!isDefault && (
          <TableDotMenu
            options={[
              {
                title: i18n.t('options.edit_button'),
                onClick: () => editFilter(data),
              },
              {
                title: i18n.t('options.delete_button'),
                onClick: () => removeFilter(data.id),
                isDeleteItem: true,
                isUnderLine: true,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
});

type Props = {
  isNoFiltersFound: boolean;
  data: HL7FilterDto[];
  isDefault: boolean;
  editFilter: (filter: HL7FilterDto) => void;
  removeFilter: (filterId: number) => void;
  onSortEnd: (event: DragEndEvent) => void;
  onEnabledChanged: (filterId: number, value: boolean) => void;
};
type RowProps = {
  listeners?: SyntheticListenerMap;
  index: number;
  data: HL7FilterDto;
  isDefault: boolean;
  editFilter: (filter: HL7FilterDto) => void;
  removeFilter: (filterId: number) => void;
  style?: CSSProperties;
  onEnabledChanged: (filterId: number, value: boolean) => void;
};

export const HL7FiltersTable: FC<Props> = React.memo(
  function HL7FiltersTable(props) {
    const i18next = useScopedTranslation('hl7.filters.list');
    const tableRef = useRef<HTMLDivElement>(null);
    const sensors = useSensors(useSensor(PointerSensor));
    const [activeId, setActiveId] = useState<number | null>(null);

    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    if (props.isNoFiltersFound) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18next.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <>
        <div ref={tableRef}>
          <div className={clsx(styles.header, styles.row)}>
            <div className={styles.info} title={i18next.t('info_tooltip')}>
              {!props.isDefault && <InfoIcon className={styles.infoIcon} />}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.message_type')}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.action')}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.enabled')}
            </div>
            <div></div>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => {
              setActiveId(e.active.id as number);
            }}
            onDragEnd={(e) => {
              props.onSortEnd(e);
              setActiveId(null);
            }}
          >
            <div>
              <SortableContext
                items={props.data.map((x, i) => x.position)}
                strategy={verticalListSortingStrategy}
              >
                {props.data.map((row, index) => (
                  <SortableRowWrapper
                    index={index}
                    key={row.id}
                    data={row}
                    isDefault={props.isDefault}
                    editFilter={props.editFilter}
                    removeFilter={props.removeFilter}
                    onEnabledChanged={props.onEnabledChanged}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
        </div>
      </>
    );
  },
);
